import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import clsx from 'clsx';

import { DimensionsWithResizeObserver } from '../../components/dimensions/dimensions';
import QuerySelect from '../../components/query-select/query-select.jsx';
import { Filters, FiltersItem } from '../../components/Filters';
import PoliciesReadout from '../../components/PoliciesReadout';
import Tooltip from '../../components/Tooltip';
import Tablist from '../../components/tablist/tablist';
import { UnconnectedTab } from '../../components/tab/tab';
import CountriesList, { CountriesListItem } from '../../components/CountriesList';
import { MapWrapper, MapWrapperMain, MapWrapperFooter } from '../../components/MapWrapper';
import PolicyMarkerLegend from '../../components/PolicyMarkerLegend';
import MapActions from '../../components/MapActions';
import ShareButton from '../../components/share-button/share-button.jsx';
import DownloadMenu from '../../components/download-menu/download-menu.jsx';
import { PoliciesQueryReadout } from '../../components/QueryReadout';
import PoliciesDescriptionsDashboard from '../../components/PoliciesDescriptionsDashboard';
import { WrappedLoader } from '../../components/loader/loader.jsx';
import PoliciesList from '../../components/PoliciesList';
import PoliciesPanel from '../../components/PoliciesPanel';
import Loader from '../../components/loader/loader.jsx';
import ChoroplethMap from '../../components/ChoroplethMap/index.js';
import PolicyDetails from '../../components/PolicyDetails/index.js';

import { matchBreakpoint } from '@chh/ui/js/matchBreakpoint.js';

import { exportMapImage, exportMapSvg } from '../../utilities/exportMapImage.js';

import { reducer, getInitialState } from './reducer.js';
import * as selectors from './selectors.js';
import { stateToQuerystring, redirectOldPolicyIds } from './utils.js';
import {
  POLICIES_EU27_COUNTRYGROUP_COUNTRY_NUMERIC_IDS,
  POLICIES_EU27_ID,
  POLICIES_EU27_TYPE_ID,
} from '../../constants.js';

import './styles.scss';

export default function Policies(props) {
  useEffect(() => {
    redirectOldPolicyIds(props.models.types);
  }, []);

  // FIXME: getInitialState is called multiple times?
  const [state, dispatch] = useReducer(reducer, getInitialState(props.models));
  const mql = matchBreakpoint('desktop');
  const [isMobile, setIsMobile] = useState(!mql.matches);

  const [imageStatus, setImageStatus] = useState('uninitialized');

  // The fixed-size map element to render off-screen for the export, so the image is
  // the same roomy layout on every breakpoint. Set from render (where the map is
  // built) and read here on click.
  const mapExportRef = useRef(null);

  // Save button: the full image, including the readout/legend/credit furniture.
  const handleSaveImage = () => exportImage({ furniture: true });

  // CMS-only: the bare map, with no readout/legend/credit.
  const handleSavePlainImage = () => exportImage({ furniture: false });

  // CMS-only: the bare map as a vector SVG.
  const handleSavePlainSvg = () => {
    if (!mapExportRef.current || imageStatus === 'pending') {
      return;
    }

    setImageStatus('pending');

    exportMapSvg({
      mapVNode: mapExportRef.current,
      svgSelector: '.chloropleth-map__svg',
      filename: 'policies-map.svg',
    }).finally(() => setImageStatus('uninitialized'));
  };

  // Export the map as a PNG. When `furniture` is false the download is just the
  // bare map, with no readout, legend or credit (for a bare-map trigger to be
  // wired up separately).
  const exportImage = ({ furniture = true } = {}) => {
    if (!mapExportRef.current || imageStatus === 'pending') {
      return;
    }

    setImageStatus('pending');

    const activeType = selectors.selectActiveType(state);

    const filename = ['resourcetransitions', 'policies', activeType.id, state.activeYear.id]
      .filter((x) => !!x)
      .join('-');

    exportMapImage({
      mapVNode: mapExportRef.current,
      svgSelector: '.chloropleth-map__svg',
      filename: `${filename}.png`,
      readoutVNode: (
        <PoliciesQueryReadout
          policyType={activeType}
          branchPolicyType={state.types.data.getBranch(activeType.id)}
          year={state.activeYear}
        />
      ),
      legendVNode: (
        <PolicyMarkerLegend
          policyType={state.types.data.getBranch(activeType.id)}
          activePolicyType={activeType}
        />
      ),
      furniture,
    }).finally(() => setImageStatus('uninitialized'));
  };

  const fetchActiveCountryData = useCallback(
    (id) => {
      dispatch({ type: 'RESULTS_REQUESTED', id: 'country' });

      fetch(`${__config.policiesApiUrl}list/${id}/`)
        .then((response) => response.json())
        .then((data) => {
          dispatch({ type: 'RESULTS_LOADED', id: 'country', data: data });
        });
    },
    [dispatch]
  );

  useEffect(() => {
    fetch(`${__config.policiesApiUrl}list`)
      .then((response) => response.json())
      .then((data) => {
        dispatch({ type: 'RESULTS_LOADED', id: 'map', data: data });
      });

    window.addEventListener('popstate', () => {
      dispatch({ type: 'HISTORY_POPSTATE' });
    });

    mql.addEventListener('change', (event) => {
      if (event.matches) {
        dispatch({ type: 'SET_ACTIVE_MOBILE_TAB', id: 'map' });
      }
      setIsMobile(!event.matches);
    });
  }, [dispatch]);

  useEffect(() => {
    if (state.activeCountry) {
      fetchActiveCountryData(state.activeCountry.id);
    }
  }, [state.activeCountry]);

  const handleHoverCountry = (id) => {
    dispatch({ type: 'SET_HOVERED_COUNTRY', id: id });
  };

  const handleClickCountry = (id) => {
    console.log('handleClickCountry', id);

    if (state.activeCountryId === id) {
      dispatch({ type: 'SET_OVERLAY_VISIBLE', visible: true });
      return;
    }

    dispatch({ type: 'SET_ACTIVE_COUNTRY', id: id });
    dispatch({ type: 'SET_HOVERED_COUNTRY', id: null });
    dispatch({ type: 'SET_OVERLAY_VISIBLE', visible: true });
  };

  const handleSelectType = (type) => {
    if (type === null) {
      dispatch({ type: 'SET_ACTIVE_TYPE', data: activeTypeBranch });
    } else {
      dispatch({ type: 'SET_ACTIVE_TYPE', data: type });
    }
  };

  const handleHoverType = (type) => {
    dispatch({ type: 'SET_HOVERED_TYPE', data: type });
  };

  const handleSelectYear = (year) => {
    dispatch({ type: 'SET_ACTIVE_YEAR', data: year });
  };

  const handleClickMobileTab = (id) => () => {
    dispatch({ type: 'SET_ACTIVE_MOBILE_TAB', id: id });
  };

  const handleSelectEconomy = (economy) => {
    if (economy === null) {
      dispatch({ type: 'SET_ACTIVE_COUNTRY', id: null });
      return;
    }

    dispatch({ type: 'SET_ACTIVE_COUNTRY', id: economy.id });
  };

  const handleClearEconomy = () => {
    dispatch({ type: 'SET_ACTIVE_COUNTRY', id: null });
  };

  const handleClickPolicy = (id) => {
    dispatch({ type: 'SET_ACTIVE_POLICY_ID', id: id });
  };

  const handleClearActivePolicy = () => {
    dispatch({ type: 'SET_ACTIVE_POLICY_ID', id: null });
  };

  if (!state.types.data || !state.results.map.data) {
    return <WrappedLoader visible />;
  }

  const activeCountryRegion = state.activeCountry
    ? state.regions.find(
        (region) => region.model.children && region.model.children.includes(state.activeCountry.id)
      )
    : null;

  // let activeCountryRegionCountries = null;
  // if (activeCountryRegion) {
  //   activeCountryRegionCountries = activeCountryRegion.model.children
  //     .map((id) => props.countryModels.get(id))
  //     .filter((model) => !!model);
  // }

  const availableCountries = selectors.selectAvailableCountriesCollection(state);
  const activeType = selectors.selectActiveType(state);
  const hoveredType = selectors.selectHoveredType(state);
  const mapData = selectors.selectFormattedMapData(state);
  const listData = selectors.selectListData(state);
  const tooltipData = selectors.selectTooltipData(state);
  const countries =
    state.activeType.id === POLICIES_EU27_TYPE_ID
      ? selectors.selectEU27Countries(state)
      : selectors.selectCountries(state);
  const hoveredCountries = selectors.selectHoveredCountries(state);
  const countryData = selectors.selectCountryData(state);
  // const activeTypeHasDescription = selectors.selectActiveTypeHasDescription(state);
  const activePolicyData = selectors.selectActivePolicyData(state);
  const activePolicyPolicyType = selectors.selectActivePolicyPolicyType(state);

  const activeTypeBranch = state.types.data.getBranch(activeType.id);

  console.log('countries', countries, hoveredCountries);
  console.log('activeTypes', activeType, activeTypeBranch);
  console.log('hoveredType', hoveredType);
  console.log('countryData', countryData);
  console.log('mapData', mapData);
  console.log('activePolicyData', activePolicyData);
  console.log('activePolicyPolicyType', activePolicyPolicyType);

  const mapNode = (
    <ChoroplethMap
      data={mapData}
      countries={countries}
      zoomCountries={
        state.activeCountry
          ? state.activeCountry.id === POLICIES_EU27_ID &&
            state.activeType.id === POLICIES_EU27_TYPE_ID
            ? countries
            : [state.activeCountry]
          : null
      }
      activeCountry={state.activeCountry}
      activeCountryIds={
        state.activeCountry && state.activeCountry.id
          ? state.activeCountry.id === POLICIES_EU27_ID &&
            state.activeType.id === POLICIES_EU27_TYPE_ID
            ? POLICIES_EU27_COUNTRYGROUP_COUNTRY_NUMERIC_IDS
            : [state.activeCountry.id]
          : null
      }
      filledCountryIds={
        state.activeCountry?.id === POLICIES_EU27_ID &&
        state.activeType.id === POLICIES_EU27_TYPE_ID
          ? POLICIES_EU27_COUNTRYGROUP_COUNTRY_NUMERIC_IDS
          : null
      }
      hoveredType={hoveredType}
      hoveredCountries={hoveredCountries}
      maxZoomScale={state.activeType.id === POLICIES_EU27_TYPE_ID ? 6.5 : 4.5}
      showAllLabels={state.activeType.id === POLICIES_EU27_TYPE_ID}
      onHoverCountry={state.activeType.id === POLICIES_EU27_TYPE_ID ? null : handleHoverCountry}
      onClickCountry={state.activeType.id === POLICIES_EU27_TYPE_ID ? null : handleClickCountry}
    />
  );

  // Expose the map for the off-screen export: wrapped in its own Dimensions so the
  // exported layout is rendered at a fixed size, independent of the current viewport.
  // forceLabels keeps the country labels (breakpoint-hidden on mobile) in the image.
  mapExportRef.current = (
    <DimensionsWithResizeObserver>
      {React.cloneElement(mapNode, { forceLabels: true })}
    </DimensionsWithResizeObserver>
  );

  const contents = state.activeCountry ? (
    <>
      <div class="policies-view__map">
        <MapWrapper>
          <div
            className={clsx(
              'policies-view__map__layout',
              !isMobile && 'policies-view__map__layout--half'
            )}
          >
            <PoliciesPanel
              country={state.activeCountry}
              policyType={activeType}
              onClickBack={
                state.activeType.id !== POLICIES_EU27_TYPE_ID ? handleClearEconomy : null
              }
            >
              {countryData && activePolicyData ? (
                <PolicyDetails
                  policy={activePolicyData}
                  policyType={activePolicyPolicyType}
                  onClickBack={handleClearActivePolicy}
                />
              ) : countryData ? (
                <PoliciesList policies={countryData} onClickPolicy={handleClickPolicy} />
              ) : (
                <div style={{ position: 'relative' }}>
                  <Loader visible />
                </div>
              )}
            </PoliciesPanel>
            {!isMobile ? (
              <div className="policies-view__map__wrapper policies-view__map__wrapper--half">
                <MapWrapperMain modifier="half">
                  <DimensionsWithResizeObserver>{mapNode}</DimensionsWithResizeObserver>
                </MapWrapperMain>
              </div>
            ) : null}
          </div>
        </MapWrapper>
      </div>
    </>
  ) : (
    <>
      <div className="policies-view__mobile-tabs">
        <Tablist>
          <UnconnectedTab
            active={state.activeMobileTab === 'map'}
            onClick={handleClickMobileTab('map')}
          >
            {__('policies-tab-map')}
          </UnconnectedTab>
          <UnconnectedTab
            active={state.activeMobileTab === 'list'}
            onClick={handleClickMobileTab('list')}
          >
            {__('policies-tab-list')}
          </UnconnectedTab>
        </Tablist>
      </div>
      <div class="policies-view__map">
        {state.activeMobileTab === 'map' && (
          <MapWrapper>
            <div className={clsx('policies-view__map__layout')}>
              <div className={clsx('policies-view__map__wrapper')}>
                <MapWrapperMain>
                  <DimensionsWithResizeObserver>{mapNode}</DimensionsWithResizeObserver>
                </MapWrapperMain>
              </div>
              <MapWrapperFooter modifier="policies">
                <PolicyMarkerLegend
                  policyType={activeTypeBranch}
                  activePolicyType={activeType}
                  hoverable={activeType === activeTypeBranch}
                  onClick={handleSelectType}
                  onHover={handleHoverType}
                />
                <MapActions>
                  <ShareButton queryString={stateToQuerystring(state)} />
                  <DownloadMenu
                    imageStatus={imageStatus}
                    onDownloadImage={handleSaveImage}
                    onDownloadPlainImage={
                      __config.allowPlainImageDownload ? handleSavePlainImage : null
                    }
                    onDownloadPlainSvg={
                      __config.allowPlainImageDownload ? handleSavePlainSvg : null
                    }
                    dataHref={__config.policiesDownloadUrl}
                  />
                </MapActions>
              </MapWrapperFooter>
            </div>
          </MapWrapper>
        )}
        {state.activeMobileTab === 'list' && (
          <div className="policies-view__mobile-list">
            <CountriesList
              data={listData}
              renderItem={(datum) => {
                if (datum.children) {
                  return datum.model.name;
                } else {
                  return (
                    <CountriesListItem
                      title={datum.model.name}
                      count={datum.data.count}
                      colour={datum.data.colour}
                      highlighted={datum.data.highlighted}
                      onClick={() => handleClickCountry(datum.model.id)}
                    />
                  );
                }
              }}
            />
          </div>
        )}
      </div>
      <div className="policies-view__dashboard">
        {activeType.model.statuses && activeType.model.statuses.length ? (
          <PoliciesDescriptionsDashboard type={activeType} />
        ) : null}
      </div>
    </>
  );

  return (
    <div className="policies-view">
      <Filters layout="policies">
        <FiltersItem>
          <QuerySelect
            id="exporter"
            label={__('policies-filter-policy')}
            current={activeType}
            data={state.types.data}
            inline
            disableParents
            handleSelect={handleSelectType}
          />
        </FiltersItem>
        <FiltersItem>
          <QuerySelect
            label={__('policies-filter-year')}
            current={state.activeYear}
            data={state.years}
            handleSelect={handleSelectYear}
          />
        </FiltersItem>
        <FiltersItem>
          <QuerySelect
            label="Zoom to"
            default={availableCountries.getDefault()}
            current={state.activeCountry || availableCountries.getDefault()}
            data={[{ name: 'Countries', data: availableCountries }]}
            handleSelect={handleSelectEconomy}
            noWrap
            searchable
            disabled={state.activeType.id === POLICIES_EU27_TYPE_ID}
          />
        </FiltersItem>
      </Filters>
      {contents}
      {state.hoveredCountry && tooltipData ? (
        <Tooltip>
          <PoliciesReadout
            country={state.hoveredCountry}
            policiesData={tooltipData.data}
            policiesTotal={tooltipData.total}
          />
        </Tooltip>
      ) : null}
    </div>
  );
}
